// @ts-nocheck
import { DashboardLayout } from "./Dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useParams, useNavigate } from "react-router";
import { ArrowLeft, RefreshCw, Settings } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useGetData } from "@/hooks/useGetData";
import AxiosInstance from "@/utils/AxiosInstance";
import { getPromise, getPromiseToast } from "@/utils/helpers";
import { useAuth } from "@/contexts/use-auth";

interface DeviceConfig {
  blood_pressure?: {
    systolic?: {
      criticalHigh: number;
      criticalLow: number;
      warningHigh: number;
      warningLow: number;
    };
    diastolic?: {
      criticalHigh: number;
      criticalLow: number;
      warningHigh: number;
      warningLow: number;
    };
    pulse?: {
      criticalHigh: number;
      criticalLow: number;
      warningHigh: number;
      warningLow: number;
    };
  };
  glucose_meter?: {
    glucose?: {
      criticalHigh: number;
      criticalLow: number;
      warningHigh: number;
      warningLow: number;
    };
  };
  pulse_oximeter?: {
    spo2?: {
      criticalLow: number;
      warningLow: number;
    };
    pulse?: {
      criticalHigh: number;
      criticalLow: number;
      warningHigh: number;
      warningLow: number;
    };
  };
}

const defaultConfig: DeviceConfig = {
  blood_pressure: {
    systolic: {
      criticalHigh: 180,
      criticalLow: 90,
      warningHigh: 160,
      warningLow: 100,
    },
    diastolic: {
      criticalHigh: 120,
      criticalLow: 60,
      warningHigh: 100,
      warningLow: 70,
    },
    pulse: {
      criticalHigh: 120,
      criticalLow: 50,
      warningHigh: 100,
      warningLow: 60,
    },
  },
  glucose_meter: {
    glucose: {
      criticalHigh: 300,
      criticalLow: 50,
      warningHigh: 250,
      warningLow: 70,
    },
  },
  pulse_oximeter: {
    spo2: {
      criticalLow: 90,
      warningLow: 95,
    },
    pulse: {
      criticalHigh: 120,
      criticalLow: 50,
      warningHigh: 100,
      warningLow: 60,
    },
  },
};

export default function DeviceSettings() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthorized } = useAuth();
  const isAdmin = user?.role?.toLowerCase() === "admin";
  const [btnLoading, setBtnLoading] = useState(false);
  const [syncReadingsLoading, setSyncReadingsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("blood-pressure");
  const [config, setConfig] = useState<DeviceConfig>(defaultConfig);

  // Fetch patient data to get patientId for header
  const {
    apiData: patientData,
    loading: patientLoading,
    fetchData: refetchPatient,
  } = useGetData(
    `/patients/${patientId}`,
    {},
    false,
    !!patientId,
  );

  const patient = patientData?.data;

  // Fetch existing configuration
  const {
    apiData: configData,
    loading: configLoading,
    fetchData: fetchConfig,
  } = useGetData(
    `/patients/${patientId}/critical-reading-thresholds`,
    {},
    false,
    !!patientId,
    (err, data) => {
      if (data && !err && data?.data?.criticalReadingThresholds) {
        // Deep merge backend data with defaults to ensure all fields are present
        const backendData = data.data.criticalReadingThresholds;
        setConfig({
          blood_pressure: {
            ...defaultConfig.blood_pressure,
            ...(backendData.blood_pressure || {}),
            systolic: {
              ...defaultConfig.blood_pressure?.systolic,
              ...(backendData.blood_pressure?.systolic || {}),
            },
            diastolic: {
              ...defaultConfig.blood_pressure?.diastolic,
              ...(backendData.blood_pressure?.diastolic || {}),
            },
            pulse: {
              ...defaultConfig.blood_pressure?.pulse,
              ...(backendData.blood_pressure?.pulse || {}),
            },
          },
          glucose_meter: {
            ...defaultConfig.glucose_meter,
            ...(backendData.glucose_meter || {}),
            glucose: {
              ...defaultConfig.glucose_meter?.glucose,
              ...(backendData.glucose_meter?.glucose || {}),
            },
          },
          pulse_oximeter: {
            ...defaultConfig.pulse_oximeter,
            ...(backendData.pulse_oximeter || {}),
            spo2: {
              ...defaultConfig.pulse_oximeter?.spo2,
              ...(backendData.pulse_oximeter?.spo2 || {}),
            },
            pulse: {
              ...defaultConfig.pulse_oximeter?.pulse,
              ...(backendData.pulse_oximeter?.pulse || {}),
            },
          },
        });
      }
      // If no data from backend, keep default values (already set in useState)
    }
  );

  const handleInputChange = (
    deviceType: keyof DeviceConfig,
    metric: string,
    field: string,
    value: string
  ) => {
    const numValue = value === "" ? "" : (parseFloat(value) || 0);
    setConfig((prev) => ({
      ...prev,
      [deviceType]: {
        ...prev[deviceType],
        [metric]: {
          ...(prev[deviceType] as any)?.[metric],
          [field]: numValue,
        },
      },
    }));
  };

  // Helper function to check if a value is valid (not empty, null, undefined, 0, or NaN)
  const isValidValue = (value: any): boolean => {
    if (value === undefined || value === null || value === "" || value === 0) {
      return false;
    }
    if (typeof value === "number" && (isNaN(value) || !isFinite(value))) {
      return false;
    }
    return true;
  };

  // Validation function to check if all fields are filled
  const validateDeviceConfig = (deviceType: keyof DeviceConfig): { isValid: boolean; errorMessage: string } => {
    const deviceConfig = config[deviceType];
    
    if (!deviceConfig) {
      return { isValid: false, errorMessage: "Device configuration is missing" };
    }

    if (deviceType === "blood_pressure") {
      const bp = deviceConfig as DeviceConfig["blood_pressure"];
      
      // Validate Systolic
      if (!isValidValue(bp?.systolic?.criticalHigh)) {
        return { isValid: false, errorMessage: "Systolic Critical High is required" };
      }
      if (!isValidValue(bp?.systolic?.criticalLow)) {
        return { isValid: false, errorMessage: "Systolic Critical Low is required" };
      }
      if (!isValidValue(bp?.systolic?.warningHigh)) {
        return { isValid: false, errorMessage: "Systolic Warning High is required" };
      }
      if (!isValidValue(bp?.systolic?.warningLow)) {
        return { isValid: false, errorMessage: "Systolic Warning Low is required" };
      }

      // Validate Diastolic
      if (!isValidValue(bp?.diastolic?.criticalHigh)) {
        return { isValid: false, errorMessage: "Diastolic Critical High is required" };
      }
      if (!isValidValue(bp?.diastolic?.criticalLow)) {
        return { isValid: false, errorMessage: "Diastolic Critical Low is required" };
      }
      if (!isValidValue(bp?.diastolic?.warningHigh)) {
        return { isValid: false, errorMessage: "Diastolic Warning High is required" };
      }
      if (!isValidValue(bp?.diastolic?.warningLow)) {
        return { isValid: false, errorMessage: "Diastolic Warning Low is required" };
      }

      // Validate Pulse
      if (!isValidValue(bp?.pulse?.criticalHigh)) {
        return { isValid: false, errorMessage: "Pulse Critical High is required" };
      }
      if (!isValidValue(bp?.pulse?.criticalLow)) {
        return { isValid: false, errorMessage: "Pulse Critical Low is required" };
      }
      if (!isValidValue(bp?.pulse?.warningHigh)) {
        return { isValid: false, errorMessage: "Pulse Warning High is required" };
      }
      if (!isValidValue(bp?.pulse?.warningLow)) {
        return { isValid: false, errorMessage: "Pulse Warning Low is required" };
      }
    }

    if (deviceType === "glucose_meter") {
      const glucose = deviceConfig as DeviceConfig["glucose_meter"];
      
      if (!isValidValue(glucose?.glucose?.criticalHigh)) {
        return { isValid: false, errorMessage: "Glucose Critical High is required" };
      }
      if (!isValidValue(glucose?.glucose?.criticalLow)) {
        return { isValid: false, errorMessage: "Glucose Critical Low is required" };
      }
      if (!isValidValue(glucose?.glucose?.warningHigh)) {
        return { isValid: false, errorMessage: "Glucose Warning High is required" };
      }
      if (!isValidValue(glucose?.glucose?.warningLow)) {
        return { isValid: false, errorMessage: "Glucose Warning Low is required" };
      }
    }

    if (deviceType === "pulse_oximeter") {
      const oximeter = deviceConfig as DeviceConfig["pulse_oximeter"];
      
      // Validate SpO2
      if (!isValidValue(oximeter?.spo2?.criticalLow)) {
        return { isValid: false, errorMessage: "SpO2 Critical Low is required" };
      }
      if (!isValidValue(oximeter?.spo2?.warningLow)) {
        return { isValid: false, errorMessage: "SpO2 Warning Low is required" };
      }

      // Validate Pulse
      if (!isValidValue(oximeter?.pulse?.criticalHigh)) {
        return { isValid: false, errorMessage: "Pulse Critical High is required" };
      }
      if (!isValidValue(oximeter?.pulse?.criticalLow)) {
        return { isValid: false, errorMessage: "Pulse Critical Low is required" };
      }
      if (!isValidValue(oximeter?.pulse?.warningHigh)) {
        return { isValid: false, errorMessage: "Pulse Warning High is required" };
      }
      if (!isValidValue(oximeter?.pulse?.warningLow)) {
        return { isValid: false, errorMessage: "Pulse Warning Low is required" };
      }
    }

    return { isValid: true, errorMessage: "" };
  };

  const handleSave = async (deviceType: keyof DeviceConfig) => {
    // Validate before saving
    const validation = validateDeviceConfig(deviceType);
    if (!validation.isValid) {
      toast.error(validation.errorMessage);
      return;
    }

    const { pr, resolveFn, rejectFn } = getPromise();
    getPromiseToast(pr);
    try {
      setBtnLoading(true);
      const payload = {
        [deviceType]: config[deviceType],
      };

      // Map device type to endpoint path
      const endpointMap: Record<string, string> = {
        blood_pressure: "blood-pressure",
        glucose_meter: "glucose-meter",
        pulse_oximeter: "pulse-oximeter",
      };

      const endpointPath = endpointMap[deviceType] || deviceType;
      const url = `/patients/${patientId}/critical-reading-thresholds/${endpointPath}`;

      const { data } = await AxiosInstance().put(url, payload);

      resolveFn(`${deviceType.replace("_", " ")} configuration saved successfully`);
    } catch (error: any) {
      rejectFn(error);
      console.log(error.message);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleSyncPatientReadings = async () => {
    if (!patientId || !isAuthorized("canAddPatients")) return;
    try {
      setSyncReadingsLoading(true);
      await AxiosInstance().post("/reading/sync/all", { patientId });
      toast.success("Readings synced successfully.");
      refetchPatient?.();
      fetchConfig?.();
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to sync readings";
      toast.error(msg);
    } finally {
      setSyncReadingsLoading(false);
    }
  };

  if (configLoading || patientLoading) {
    return (
      <DashboardLayout activePage="/patients/" entityId={patientId}>
        <div className="w-full text-center p-6 text-muted-foreground">Loading device settings...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      activePage="/patients/"
      entityId={patient?.patientId || patientId}
      rightcomponent={
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/patients/${patientId}`)}
          className="border-primary text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Profile
        </Button>
      }
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-3xl font-bold tracking-tight">Device Settings</h2>
            {isAuthorized("canAddPatients") && (
              <Button
                variant="outline"
                size="sm"
                disabled={syncReadingsLoading}
                onClick={handleSyncPatientReadings}
                className="border-secondary text-secondary hover:bg-secondary/10 hover:text-secondary shrink-0 w-full sm:w-auto"
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${syncReadingsLoading ? "animate-spin" : ""}`}
                />
                Sync data
              </Button>
            )}
          </div>
          <p className="text-muted-foreground">
            Configure critical and warning thresholds for patient devices
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="blood-pressure">Blood Pressure</TabsTrigger>
            <TabsTrigger value="glucose-meter">Glucose Meter</TabsTrigger>
            <TabsTrigger value="pulse-oximeter">Pulse Oximeter</TabsTrigger>
            <TabsTrigger value="general">General</TabsTrigger>
          </TabsList>

          {/* Blood Pressure Tab */}
          <TabsContent value="blood-pressure" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Blood Pressure Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Systolic */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Systolic Blood Pressure</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="systolic-critical-high">Critical High</Label>
                      <Input
                        id="systolic-critical-high"
                        type="number"
                        required
                        value={config.blood_pressure?.systolic?.criticalHigh || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "blood_pressure",
                            "systolic",
                            "criticalHigh",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="systolic-critical-low">Critical Low</Label>
                      <Input
                        id="systolic-critical-low"
                        type="number"
                        required
                        value={config.blood_pressure?.systolic?.criticalLow || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "blood_pressure",
                            "systolic",
                            "criticalLow",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="systolic-warning-high">Warning High</Label>
                      <Input
                        id="systolic-warning-high"
                        type="number"
                        required
                        value={config.blood_pressure?.systolic?.warningHigh || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "blood_pressure",
                            "systolic",
                            "warningHigh",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="systolic-warning-low">Warning Low</Label>
                      <Input
                        id="systolic-warning-low"
                        type="number"
                        required
                        value={config.blood_pressure?.systolic?.warningLow || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "blood_pressure",
                            "systolic",
                            "warningLow",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Diastolic */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Diastolic Blood Pressure</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="diastolic-critical-high">Critical High</Label>
                      <Input
                        id="diastolic-critical-high"
                        type="number"
                        required
                        value={config.blood_pressure?.diastolic?.criticalHigh || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "blood_pressure",
                            "diastolic",
                            "criticalHigh",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="diastolic-critical-low">Critical Low</Label>
                      <Input
                        id="diastolic-critical-low"
                        type="number"
                        required
                        value={config.blood_pressure?.diastolic?.criticalLow || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "blood_pressure",
                            "diastolic",
                            "criticalLow",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="diastolic-warning-high">Warning High</Label>
                      <Input
                        id="diastolic-warning-high"
                        type="number"
                        required
                        value={config.blood_pressure?.diastolic?.warningHigh || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "blood_pressure",
                            "diastolic",
                            "warningHigh",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="diastolic-warning-low">Warning Low</Label>
                      <Input
                        id="diastolic-warning-low"
                        type="number"
                        required
                        value={config.blood_pressure?.diastolic?.warningLow || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "blood_pressure",
                            "diastolic",
                            "warningLow",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Pulse */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Pulse (BPM)</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="pulse-critical-high">Critical High</Label>
                      <Input
                        id="pulse-critical-high"
                        type="number"
                        required
                        value={config.blood_pressure?.pulse?.criticalHigh || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "blood_pressure",
                            "pulse",
                            "criticalHigh",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pulse-critical-low">Critical Low</Label>
                      <Input
                        id="pulse-critical-low"
                        type="number"
                        required
                        value={config.blood_pressure?.pulse?.criticalLow || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "blood_pressure",
                            "pulse",
                            "criticalLow",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pulse-warning-high">Warning High</Label>
                      <Input
                        id="pulse-warning-high"
                        type="number"
                        required
                        value={config.blood_pressure?.pulse?.warningHigh || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "blood_pressure",
                            "pulse",
                            "warningHigh",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pulse-warning-low">Warning Low</Label>
                      <Input
                        id="pulse-warning-low"
                        type="number"
                        required
                        value={config.blood_pressure?.pulse?.warningLow || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "blood_pressure",
                            "pulse",
                            "warningLow",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  {isAdmin && (
                    <Button
                      onClick={() => handleSave("blood_pressure")}
                      disabled={btnLoading}
                    >
                      {btnLoading ? "Submitting..." : "Submit"}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Glucose Meter Tab */}
          <TabsContent value="glucose-meter" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Glucose Meter Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Glucose Level (mg/dL)</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="glucose-critical-high">Critical High</Label>
                      <Input
                        id="glucose-critical-high"
                        type="number"
                        required
                        value={config.glucose_meter?.glucose?.criticalHigh || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "glucose_meter",
                            "glucose",
                            "criticalHigh",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="glucose-critical-low">Critical Low</Label>
                      <Input
                        id="glucose-critical-low"
                        type="number"
                        required
                        value={config.glucose_meter?.glucose?.criticalLow || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "glucose_meter",
                            "glucose",
                            "criticalLow",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="glucose-warning-high">Warning High</Label>
                      <Input
                        id="glucose-warning-high"
                        type="number"
                        required
                        value={config.glucose_meter?.glucose?.warningHigh || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "glucose_meter",
                            "glucose",
                            "warningHigh",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="glucose-warning-low">Warning Low</Label>
                      <Input
                        id="glucose-warning-low"
                        type="number"
                        required
                        value={config.glucose_meter?.glucose?.warningLow || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "glucose_meter",
                            "glucose",
                            "warningLow",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  {isAdmin && (
                    <Button
                      onClick={() => handleSave("glucose_meter")}
                      disabled={btnLoading}
                    >
                      {btnLoading ? "Submitting..." : "Submit"}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pulse Oximeter Tab */}
          <TabsContent value="pulse-oximeter" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Pulse Oximeter Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* SpO2 */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">SpO2 (%)</h3>
                  <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="spo2-critical-low">Critical Low</Label>
                      <Input
                        id="spo2-critical-low"
                        type="number"
                        required
                        value={config.pulse_oximeter?.spo2?.criticalLow || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "pulse_oximeter",
                            "spo2",
                            "criticalLow",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="spo2-warning-low">Warning Low</Label>
                      <Input
                        id="spo2-warning-low"
                        type="number"
                        required
                        value={config.pulse_oximeter?.spo2?.warningLow || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "pulse_oximeter",
                            "spo2",
                            "warningLow",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Pulse */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Pulse (BPM)</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="oximeter-pulse-critical-high">Critical High</Label>
                      <Input
                        id="oximeter-pulse-critical-high"
                        type="number"
                        required
                        value={config.pulse_oximeter?.pulse?.criticalHigh || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "pulse_oximeter",
                            "pulse",
                            "criticalHigh",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="oximeter-pulse-critical-low">Critical Low</Label>
                      <Input
                        id="oximeter-pulse-critical-low"
                        type="number"
                        required
                        value={config.pulse_oximeter?.pulse?.criticalLow || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "pulse_oximeter",
                            "pulse",
                            "criticalLow",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="oximeter-pulse-warning-high">Warning High</Label>
                      <Input
                        id="oximeter-pulse-warning-high"
                        type="number"
                        required
                        value={config.pulse_oximeter?.pulse?.warningHigh || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "pulse_oximeter",
                            "pulse",
                            "warningHigh",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="oximeter-pulse-warning-low">Warning Low</Label>
                      <Input
                        id="oximeter-pulse-warning-low"
                        type="number"
                        required
                        value={config.pulse_oximeter?.pulse?.warningLow || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "pulse_oximeter",
                            "pulse",
                            "warningLow",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  {isAdmin && (
                    <Button
                      onClick={() => handleSave("pulse_oximeter")}
                      disabled={btnLoading}
                    >
                      {btnLoading ? "Submitting..." : "Submit"}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>General Device Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Additional device configurations will be available here in the future.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
